import { formatMoney } from '@nemuru-eng/js-api-client';

import {
  COMPANY_ACTIVITY_TYPE_ARRAY,
  EMPLOYMENT_STATUS_TYPE_ARRAY,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/constants';
import { seniorityDateFormatter } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/utils/seniorityDateToMonthsAndYears';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { CreditCardBorrowerOnboardingDetails, PendingDataType } from '@/config/types';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

interface ProfessionalDataProps extends FormGridOptions {
  onboardingDetails: CreditCardBorrowerOnboardingDetails;
  editModeAllowed?: boolean;
}

export const ProfessionalData = ({ onboardingDetails, fieldVariant, fullWidth, direction }: ProfessionalDataProps) => {
  const professionalDetails = {
    companyInfo: onboardingDetails.companyInfo,
    employmentStatus: onboardingDetails.employmentStatus,
  };
  const { getTextIdTranslation } = useTranslation();

  if (
    !professionalDetails ||
    !professionalDetails.employmentStatus ||
    !onboardingDetails.grossIncome ||
    !onboardingDetails.monthlyExpenses
  ) {
    return (
      <SubSectionWrapper title={'PROFESSIONAL_DATA'}>
        <PendingDataWarning type={PendingDataType.DEFAULT} />
      </SubSectionWrapper>
    );
  }

  return (
    <SubSectionWrapper title={'PROFESSIONAL_DATA'}>
      <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth} direction={direction}>
        <AutocompleteSelectFormField
          name={'employmentStatus'}
          label={'EMPLOYMENT_STATUS'}
          value={getTextIdTranslation(professionalDetails.employmentStatus)}
          selectOptions={EMPLOYMENT_STATUS_TYPE_ARRAY(getTextIdTranslation)}
          fieldVariant={fieldVariant}
        />
        <AutocompleteSelectFormField
          name={'companyActivity'}
          label={'COMPANY_ACTIVITY'}
          value={
            professionalDetails.companyInfo?.activity && getTextIdTranslation(professionalDetails.companyInfo.activity)
          }
          selectOptions={COMPANY_ACTIVITY_TYPE_ARRAY(getTextIdTranslation)}
          fieldVariant={fieldVariant}
        />
        <TextFormField
          name={'companyName'}
          label={'COMPANY_NAME'}
          value={professionalDetails.companyInfo?.name}
          fieldVariant={fieldVariant}
        />
        <DateFormField
          name={'seniority'}
          label={'SENIORITY'}
          placeholder={getTextIdTranslation('DATE_FORMAT_MONTH_YEAR')}
          views={['year', 'month']}
          format={'MM/yyyy'}
          value={seniorityDateFormatter.monthsAndYearsToDate(
            professionalDetails.companyInfo?.seniority.months,
            professionalDetails.companyInfo?.seniority.years,
          )}
          fieldVariant={fieldVariant}
          disableFuture
        />
        <TextFormField
          label="GROSS_INCOME"
          name="grossIncome"
          value={formatMoney(amountToValueMoneyMapper(onboardingDetails.grossIncome))}
        />
        <TextFormField
          label="MONTHLY_EXPENSES"
          name="monthlyExpenses"
          value={formatMoney(amountToValueMoneyMapper(onboardingDetails.monthlyExpenses))}
        />
      </FormGridContainer>
    </SubSectionWrapper>
  );
};
